import { Injectable, inject } from '@angular/core';
import { Analysis, FileOverviewItem } from '@shared/models/analysis.model';
import { AnalysisService } from '@shared/services/analysis.service';
import { StartAnalysisDialogService } from '@shared/services/start-analysis-dialog';
import { stagesNeedingSelection } from '@features/analyses/config/pipeline-stage.config';

/**
 * The single gate every execution path goes through before dispatching tasks.
 *
 * A task window can cover stages that need a selection the worker reads off the
 * SI analysis row — the input file for detection (tasks 2-3), the planning
 * template for placement/planning (tasks 4-5). This asks for each of them, in
 * task order, and each dialog persists its choice to SI on confirm. Callers
 * dispatch exactly one execute call afterwards.
 *
 * Which stages need what lives in PIPELINE_STAGE_CONFIGS — not here.
 */
@Injectable({ providedIn: 'root' })
export class ExecutionPreflightService {
  private readonly analysisService = inject(AnalysisService);
  private readonly executionDialog = inject(StartAnalysisDialogService);

  /**
   * @param filesOverview Pass it if the caller already has it; fetched on demand otherwise.
   * @returns false as soon as one dialog is cancelled — the caller must not execute.
   */
  async confirm(
    analysis: Analysis,
    organizationId: string,
    taskWindow: { startTask: number; endTask: number },
    filesOverview?: FileOverviewItem[],
  ): Promise<boolean> {
    const stages = stagesNeedingSelection(taskWindow);
    const mediaStage = stages.find((stage) => stage.startDialog === 'file-selection');
    const requiresTemplate = stages.some((stage) => stage.startDialog === 'template-selection');
    const prospective = !!mediaStage && taskWindow.startTask < mediaStage.tasks[0];
    const files = !mediaStage || prospective
      ? []
      : (filesOverview ?? await this.analysisService.getFilesOverview(
          organizationId,
          analysis.analysisId,
        ));

    return this.executionDialog.confirmExecution({
      analysis,
      organizationId,
      taskWindow,
      filesOverview: files,
      currentSelectedMedia:
        analysis.selectedMediaId && analysis.selectedMediaType
          ? {
              mediaId: analysis.selectedMediaId,
              mediaType: analysis.selectedMediaType,
            }
          : null,
      currentSelectedTemplateId: analysis.selectedTemplateId ?? null,
      prospective,
      requiresMedia: !!mediaStage,
      requiresTemplate,
    });
  }
}
